import React from 'react';
import { Activity, Award, BarChart3, Clock, Download, Zap } from 'lucide-react';
import { StatCard } from '../SharedComponents';

const EngagementTab = ({ ctx }) => {
  const { usage, members, usageDetail, API_URL, getToken } = ctx;

  return (
<div data-testid="engagement-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Team Engagement</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Per-member usage breakdown. Track time spent, sessions, challenges completed, and identify members who may need encouragement.
  </p>

  {usageDetail?.demo_data && (
    <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium mb-4" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
      <BarChart3 size={14} />
      Sample data — will reflect real team activity once members are active
    </div>
  )}

  {/* Export button */}
  <div className="flex justify-end mb-3">
    <button
      onClick={() => {
        const token = getToken();
        fetch(`${API_URL}/api/corporate/export/engagement`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'engagement_export.csv'; a.click(); URL.revokeObjectURL(u); });
      }}
      className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
      style={{ backgroundColor: '#F0F0F0', color: '#555' }} data-testid="export-engagement-csv"
    >
      <Download size={12} /> Export CSV
    </button>
  </div>

  {/* Summary stats */}
  {usageDetail?.students?.length > 0 && (() => {
    const sts = usageDetail.students;
    const totalMins = sts.reduce((s, x) => s + x.total_minutes_30d, 0);
    const totalSessions = sts.reduce((s, x) => s + x.sessions_30d, 0);
    const totalChallenges = sts.reduce((s, x) => s + x.challenges_completed, 0);
    const activeCount = sts.filter(x => x.days_inactive === null || x.days_inactive < 7).length;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Usage (30d)" value={`${Math.round(totalMins / 60)}h ${totalMins % 60}m`} icon={<Clock size={14} style={{ color: '#2563EB' }} />} />
        <StatCard label="Total Sessions" value={totalSessions} icon={<Activity size={14} style={{ color: '#F5831F' }} />} />
        <StatCard label="Challenges Done" value={totalChallenges} icon={<Award size={14} style={{ color: '#10B981' }} />} />
        <StatCard label="Active Members" value={`${activeCount} / ${sts.length}`} icon={<Zap size={14} style={{ color: '#8B5CF6' }} />} />
      </div>
    );
  })()}

  {/* Usage table */}
  {usageDetail?.students?.length > 0 ? (
    <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr style={{ backgroundColor: '#1A1209' }}>
              {['Member', 'Department', 'Time (30d)', 'Time (7d)', 'Sessions', 'Challenges', 'Skills', 'Avg / Session', 'Last Active', 'Status'].map(h => (
                <th key={h} className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/60">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(usageDetail.members || usageDetail.students).map((s, i) => {
              const status = s.days_inactive === null ? 'new' :
                s.days_inactive === 0 ? 'active' :
                s.days_inactive <= 3 ? 'recent' :
                s.days_inactive <= 7 ? 'fading' : 'inactive';
              const statusStyle = {
                active: { bg: '#ECFDF5', color: '#065F46', label: 'Active' },
                recent: { bg: '#F0FDF4', color: '#15803D', label: 'Recent' },
                fading: { bg: '#FFF7ED', color: '#9A3412', label: 'Fading' },
                inactive: { bg: '#FEF2F2', color: '#991B1B', label: 'Inactive' },
                new: { bg: '#F0F9FF', color: '#1E40AF', label: 'New' },
              }[status];
              return (
                <tr key={s.student_id} className="border-b" style={{ borderColor: '#F3F4F6', backgroundColor: i % 2 === 0 ? '#FAFAFA' : 'white' }}>
                  <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#1A1209' }}>{s.first_name} {s.last_initial}</td>
                  <td className="px-3 py-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#2563EB15', color: '#2563EB' }}>{s.grade}</span></td>
                  <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#1A1209' }}>{s.total_minutes_30d}m</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.minutes_7d}m</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.sessions_30d}</td>
                  <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#F5831F' }}>{s.challenges_completed}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.skills_practiced}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: '#666' }}>{s.avg_session_min}m</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: '#999' }}>
                    {s.last_active ? (() => {
                      const d = s.days_inactive;
                      if (d === 0) return 'Today';
                      if (d === 1) return 'Yesterday';
                      if (d < 7) return `${d}d ago`;
                      return new Date(s.last_active).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                    })() : 'Never'}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}>
                      {statusStyle.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Activity size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
      <p className="text-sm" style={{ color: '#999' }}>No engagement data yet. Students will appear here once they start training.</p>
    </div>
  )}
</div>
  );
};

export default EngagementTab;
